import {
  WSSelect,
  WSSelectOption,
  WSSelectPropsSingle
} from "@wingspanhq/fe-component-library";
import { PayerEngagementStatus } from "@wingspanhq/wingspan-api-sdk";
import { useEffect, useMemo } from "react";
import { queriesPayerEngagements } from "@query";

type Props = Omit<
  WSSelectPropsSingle,
  "options" | "value" | "onChange" | "mode"
> & {
  payerId?: string | null;
  value: string | null;
  onChange: (value: string | null) => void;
  /**
   * Select the only option as soon as it resolves. Most clients have a single
   * assignment, so this keeps the two-picker flow a one-click flow for them.
   */
  autoSelectSingleOption?: boolean;
};

/**
 * Assignment (payer-engagement) picker for an already-selected client.
 *
 * Reads the regular `listPayerEngagements` collection rather than the payer
 * search rows: the engagements belong to one known payer, so there is nothing to
 * search across and the list endpoint is authoritative the moment an engagement
 * is created (the search projection lags).
 */
export const SelectEngagementForPayer: React.FC<Props> = props => {
  const { payerId, value, onChange, autoSelectSingleOption, ...selectProps } =
    props;

  const queryEngagements =
    queriesPayerEngagements.listPayerEngagements.useQueryAll(
      { payerId: payerId as string },
      { enabled: !!payerId }
    );

  const options = useMemo<WSSelectOption[]>(
    () =>
      (queryEngagements.flatData ?? [])
        .filter(
          engagement =>
            // The selected engagement stays listed whatever its status, so an
            // invoice on a since-archived assignment still renders its name on
            // the disabled (edit-mode) picker.
            engagement.id === value ||
            (engagement.status === PayerEngagementStatus.ACTIVATED &&
              // The v1 `labels.duplicate` flag lives on the v3 `metadata` map,
              // whose values are strings — the boolean `true` becomes `"true"`.
              engagement.metadata?.duplicate !== "true")
        )
        .map(engagement => ({
          value: engagement.id as string,
          label: engagement.engagementName || "Assignment"
        })),
    [queryEngagements.flatData, value]
  );

  // Resolves back to undefined once the value lands, so this settles in one pass.
  const singleOptionValue =
    autoSelectSingleOption &&
    !value &&
    queryEngagements.isSuccess &&
    options.length === 1
      ? options[0].value
      : undefined;

  useEffect(() => {
    if (singleOptionValue) {
      onChange(singleOptionValue);
    }
  }, [singleOptionValue, onChange]);

  return (
    <WSSelect
      {...selectProps}
      mode="single"
      value={value}
      onChange={newValue => onChange(newValue ?? null)}
      placeholder={selectProps.placeholder ?? "Select an option"}
      options={options}
      internalSearch={selectProps.internalSearch ?? true}
      // Splitting the pickers makes a client with no selectable assignment
      // reachable, where the combined picker simply omitted it — say so instead
      // of dropping the user into an empty menu.
      emptyState={
        selectProps.emptyState ?? {
          type: "documents",
          title: "No assignments available",
          description:
            "This client has no active assignment to invoice against yet."
        }
      }
      status={
        queryEngagements.isInitialLoading ? "loading" : selectProps.status
      }
      isListLoading={queryEngagements.isLoading}
    />
  );
};
